import { LoadingButton } from '@mui/lab';
import { Card, CardContent, CardHeader, Chip, Stack, Typography } from '@mui/material';
import { enqueueSnackbar } from 'notistack';

import { useTranslate } from 'src/locales';
import Iconify from 'src/components/iconify';
import { useGetIntegrationQuery, useSetupCommunicationsMutation } from 'src/services/integration/integrations.service';
import { IntegrationStatus } from 'src/services/integration/integrations.types';

// ----------------------------------------------------------------------

type Props = {
  integrationId: string;
};

export default function DialpadSettings({ integrationId }: Props) {
  const { t } = useTranslate();

  const { data: integration } = useGetIntegrationQuery({ integrationId }, { skip: !integrationId });
  const [setup, { isLoading }] = useSetupCommunicationsMutation();

  const handleSetup = async () => {
    try {
      await setup().unwrap();
      enqueueSnackbar(t('integrations.communications.config.setup_success'), { variant: 'success' });
    } catch (e: any) {
      enqueueSnackbar(e?.data?.message || t('integrations.communications.config.setup_error'), { variant: 'error' });
    }
  };

  return (
    <Card>
      <CardHeader
        title={t('integrations.communications.config.title')}
        subheader={t('integrations.communications.config.subtitle')}
      />
      <CardContent>
        <Stack spacing={2.5}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="subtitle2">{t('integrations.communications.config.status')}</Typography>
            <Chip
              size="small"
              color={integration?.status === IntegrationStatus.ACTIVE ? 'success' : 'warning'}
              label={integration?.status ?? '—'}
              sx={{ textTransform: 'capitalize' }}
            />
          </Stack>

          <div>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {t('integrations.communications.config.capabilities')}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip size="small" variant="outlined" icon={<Iconify icon="solar:phone-bold" />} label={t('integrations.communications.config.cap_calls')} />
              <Chip size="small" variant="outlined" icon={<Iconify icon="solar:chat-round-dots-bold" />} label={t('integrations.communications.config.cap_sms')} />
              <Chip size="small" variant="outlined" icon={<Iconify icon="solar:document-text-bold" />} label={t('integrations.communications.config.cap_transcripts')} />
            </Stack>
          </div>

          <Typography variant="body2" color="text.secondary">
            {t('integrations.communications.config.webhooks_help')}
          </Typography>

          <Stack direction="row">
            <LoadingButton
              variant="outlined"
              loading={isLoading}
              onClick={handleSetup}
              startIcon={<Iconify icon="solar:refresh-bold" />}
            >
              {t('integrations.communications.config.setup_button')}
            </LoadingButton>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}
